import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    Box,
    withStyles
} from '@material-ui/core';
import { ArrowDownward, ArrowUpward } from '@material-ui/icons';

const styles = (theme) => ({
    tableContainer: {
        marginBottom: theme.spacing(2),
        overflow: 'hidden',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    },
    table: {
        width: '100%',
        '& .MuiTableCell-root': {
            fontSize: 14,
            color: '#212529',
            borderBottom: '1px solid #E9ECEF',
        },
    },
    tableHeader: {
        backgroundColor: '#F8F9FA',
    },
    headerCell: {
        fontWeight: 600,
        userSelect: 'none',
        whiteSpace: 'nowrap',
    },
    sortableHeader: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(0,168,232,0.05)',
        },
    },
    row: {
        '&:hover': {
            backgroundColor: 'rgba(0,168,232,0.03)',
        },
    },
    emptyCell: {
        textAlign: 'center',
        padding: theme.spacing(3),
        color: '#6C757D',
        fontStyle: 'italic',
    },
    sortIcon: {
        fontSize: 16,
        marginLeft: theme.spacing(0.5),
    },
});

const compareValues = (a, b, order) => {
    const valueA = a == null ? '' : String(a).toLowerCase();
    const valueB = b == null ? '' : String(b).toLowerCase();

    if (valueA < valueB) return order === 'asc' ? -1 : 1;
    if (valueA > valueB) return order === 'asc' ? 1 : -1;
    return 0;
};

function NewCustomTable({
    classes,
    columns,
    rows,
    rowKey,
    rowsPerPage,
    emptyMessage,
    currentLanguage,
    defaultSortField,
    defaultSortOrder,
}) {
    const [page, setPage] = useState(0);
    const [sortField, setSortField] = useState(defaultSortField || null);
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);

    const sortedRows = useMemo(() => {
        if (!sortField) return rows;

        const column = columns.find((item) => item.key === sortField);
        if (!column) return rows;

        const getter = column.sortValue || ((row) => row[sortField]);
        return [...rows].sort((rowA, rowB) => compareValues(getter(rowA), getter(rowB), sortOrder));
    }, [rows, columns, sortField, sortOrder]);

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedRows.slice(start, start + rowsPerPage);
    }, [sortedRows, page, rowsPerPage]);

    const handleSort = (column) => {
        if (!column.sortable) return;

        if (sortField === column.key) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setSortField(column.key);
        setSortOrder('asc');
    };

    const totalRows = sortedRows.length;
    const fallbackEmptyMessage =
        emptyMessage ||
        currentLanguage?.bepiService?.shared?.shared_flat_table_empty_msg ||
        'Nenhum registro encontrado';

    return (
        <Paper className={classes.tableContainer}>
            <Table className={classes.table} size="small">
                <TableHead>
                    <TableRow className={classes.tableHeader}>
                        {columns.map((column) => {
                            const isSorted = sortField === column.key;
                            return (
                                <TableCell
                                    key={column.key}
                                    align={column.align || 'left'}
                                    className={`${classes.headerCell} ${column.sortable ? classes.sortableHeader : ''}`}
                                    onClick={() => handleSort(column)}
                                    style={{ width: column.width }}
                                >
                                    <Box display="inline-flex" alignItems="center">
                                        {column.label}
                                        {column.sortable && isSorted && (
                                            sortOrder === 'asc' ?
                                                <ArrowUpward className={classes.sortIcon} /> :
                                                <ArrowDownward className={classes.sortIcon} />
                                        )}
                                    </Box>
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {paginatedRows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={columns.length} className={classes.emptyCell}>
                                {fallbackEmptyMessage}
                            </TableCell>
                        </TableRow>
                    ) : (
                        paginatedRows.map((row, rowIndex) => (
                            <TableRow
                                key={typeof rowKey === 'function' ? rowKey(row, rowIndex) : row[rowKey] || rowIndex}
                                className={classes.row}
                            >
                                {columns.map((column) => (
                                    <TableCell key={`${column.key}-${rowIndex}`} align={column.align || 'left'}>
                                        {column.render ? column.render(row, rowIndex) : row[column.key]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            {totalRows > rowsPerPage && (
                <TablePagination
                    component="div"
                    count={totalRows}
                    page={page}
                    onChangePage={(_, nextPage) => setPage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    onChangeRowsPerPage={() => {}}
                    rowsPerPageOptions={[rowsPerPage]}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            )}
        </Paper>
    );
}

NewCustomTable.propTypes = {
    classes: PropTypes.object.isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.node.isRequired,
            sortable: PropTypes.bool,
            render: PropTypes.func,
            sortValue: PropTypes.func,
            align: PropTypes.oneOf(['left', 'center', 'right', 'justify', 'inherit']),
            width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    rowsPerPage: PropTypes.number,
    emptyMessage: PropTypes.string,
    currentLanguage: PropTypes.object,
    defaultSortField: PropTypes.string,
    defaultSortOrder: PropTypes.oneOf(['asc', 'desc']),
};

NewCustomTable.defaultProps = {
    rowKey: 'id',
    rowsPerPage: 5,
    emptyMessage: undefined,
    currentLanguage: undefined,
    defaultSortField: null,
    defaultSortOrder: 'asc',
};

export default withStyles(styles)(NewCustomTable);
